import { useId, useRef } from 'react';
import { TextInput, TextInputProps, View } from 'react-native';
import { QuantityItemProps } from '../QuantityItem.types';
import { PressableHovered } from 'src/editor-v3/components/pressable-hovered/PressableHovered';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { FocusGroups, PRODUCT_FOCUS_ORDER_STEP } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { productContentFooterTestID } from 'src/helpers/testIDs.helper';
import { icon } from 'src/theme/colors';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { useFocus } from 'src/hooks/useFocus';
import { useFocusContext } from 'src/contexts/FocusContext';
import { styles } from './QuantityItemDesktop.styles';
import { useQuantityItemLogic } from '../QuantityItem.logic';

const FocusableQuantityInput = ({
  order,
  onBlur,
  onKeyPress,
  ...props
}: TextInputProps & { order: number }) => {
  const ref = useRef(null);
  const id = useId();
  const { blur } = useFocus({ id, ref, order, group: FocusGroups.PRODUCT, focusable: true });
  const { focusCloseFocusableElement } = useFocusContext();
  return (
    <TextInput
      ref={ref}
      {...props}
      onBlur={e => {
        blur();
        onBlur?.(e);
      }}
      onKeyPress={(e: any) => {
        if (e.nativeEvent.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          focusCloseFocusableElement(!e.nativeEvent.shiftKey);
        }
        onKeyPress?.(e);
      }}
    />
  );
};

export const QuantityItemDesktop = observer((props: QuantityItemProps) => {
  const { i18n } = useStore();
  const { containerStyle, product, index = 0 } = props;
  const focusOrderBase = index * PRODUCT_FOCUS_ORDER_STEP;
  const {
    productCount,
    isTextInputFocused,
    onChangeProductCountLocale,
    addProduct,
    subtractProduct,
    removeProduct,
    updateProduct,
    setIsTextInputFocused,
  } = useQuantityItemLogic(product);

  return (
    <View style={containerStyle}>
      <PressableHovered
        testID={
          productCount === 1
            ? productContentFooterTestID(product.id, TestIDs.REMOVE)
            : productContentFooterTestID(product.id, TestIDs.SUBTRACT)
        }
        onPress={() => {
          if (productCount === 1) removeProduct({ sectionName: SECTION_NAMES.PICTURE_VIEW });
          else subtractProduct();
        }}
        style={styles.icon}
        hint={productCount === 1 ? i18n.t('actions:delete') : i18n.t('actions:decrease')}
        hintStyle={{ bottom: -30, left: '50%', transform: [{ translateX: '-50%' }] }}
        focusGroup={FocusGroups.PRODUCT}
        focusOrder={focusOrderBase + 4}
      >
        <View style={styles.iconAction}>
          <Icon name={productCount === 1 ? ICON_NAMES.Delete : ICON_NAMES.Minus} color={icon.iconInteractive} />
        </View>
      </PressableHovered>

      <FocusableQuantityInput
        order={focusOrderBase + 5}
        testID={productContentFooterTestID(product.id, TestIDs.PRODUCT_COUNT)}
        style={[
          styles.countText,
          { minWidth: 8 + productCount.toString().length * 9 },
          isTextInputFocused && styles.countTextFocused,
        ]}
        value={productCount.toString()}
        onChangeText={onChangeProductCountLocale}
        onBlur={() => {
          updateProduct();
          setIsTextInputFocused(false);
        }}
        onFocus={() => setIsTextInputFocused(true)}
      />

      <PressableHovered
        testID={productContentFooterTestID(product.id, TestIDs.INCREASE)}
        onPress={addProduct}
        style={styles.icon}
        hint={i18n.t('actions:increase')}
        hintStyle={{ bottom: -30, left: '50%', transform: [{ translateX: '-50%' }] }}
        disabled={productCount === 9999}
        focusGroup={FocusGroups.PRODUCT}
        focusOrder={focusOrderBase + 6}
      >
        <View style={styles.iconAction}>
          <Icon name={ICON_NAMES.Plus} color={productCount === 9999 ? icon.iconColorDisabled : icon.iconInteractive} />
        </View>
      </PressableHovered>
    </View>
  );
});

QuantityItemDesktop.displayName = 'QuantityItemDesktop';
